import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import ChatSidebar from "../components/ChatSidebar";
import AnswerDisplay from "../components/AnswerDisplay";
import LawSourcePanel from "../components/LawSourcePanel";
import LeadForm from "../components/LeadForm";
import LoadingSpinner from "../components/LoadingSpinner";
import ThinkingProgress from "../components/ThinkingProgress";
import BrandIcon from "../components/BrandIcon";
import {
  askQuestion,
  submitLead,
  fetchChatSessions,
  fetchSessionMessages,
  deleteChatSession,
} from "../api";
import { useAuth } from "../contexts/AuthContext";

const EXAMPLE_QUESTIONS = [
  { cat: "LAW", label: "전세 사기를 당했어요", q: "전세 사기를 당하면 어떻게 해야 하나요?", icon: "law" },
  { cat: "LAW", label: "부당해고에 대응하고 싶어요", q: "직장에서 부당해고를 당했는데 어떻게 대응하나요?", icon: "law" },
  { cat: "TAX", label: "종합소득세 신고가 궁금해요", q: "프리랜서인데 종합소득세 신고는 어떻게 하나요?", icon: "tax" },
];

const HOME_TOOLS = [
  { to: "/document-analysis", label: "문서 분석", icon: "document" },
  { to: "/prediction", label: "결과 예측", icon: "prediction" },
  { to: "/draft", label: "서류 작성", icon: "draft" },
];

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // OAuth 에러 표시
  const oauthError = searchParams.get('error');
  useEffect(() => {
    if (oauthError) {
      const timer = setTimeout(() => {
        searchParams.delete('error');
        setSearchParams(searchParams, { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [oauthError, searchParams, setSearchParams]);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Input state
  const [category, setCategory] = useState("LAW");
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Session loading state
  const [sessionLoading, setSessionLoading] = useState(false);
  const loadingSessionRef = useRef(null);

  // Thinking steps for SSE streaming
  const [thinkingSteps, setThinkingSteps] = useState([]);

  // Lead form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load sessions on mount
  const loadSessions = useCallback(async () => {
    if (!user) return;
    try {
      const data = await fetchChatSessions();
      setSessions(data);
    } catch (err) {
      console.error("세션 목록 로드 실패:", err);
    }
  }, [user]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length) messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading]);

  // Auto-resize textarea helper
  const autoResize = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 150) + 'px';
  }, []);
  useEffect(() => { autoResize(inputRef.current); }, [question, autoResize]);

  const handleNewChat = () => {
    loadingSessionRef.current = null;
    setCurrentSessionId(null);
    setMessages([]);
    setError("");
    setQuestion("");
    setSidebarOpen(false);
    setSessionLoading(false);
  };

  const handleSelectSession = async (sessionId) => {
    loadingSessionRef.current = sessionId;
    setCurrentSessionId(sessionId);
    setError("");
    setQuestion("");
    setSidebarOpen(false);
    setMessages([]);
    setSessionLoading(true);

    try {
      const msgs = await fetchSessionMessages(sessionId);
      // 다른 세션이 이미 선택된 경우 무시 (race condition 방지)
      if (loadingSessionRef.current !== sessionId) return;

      setMessages(
        msgs.map((m) => ({
          id: m.id,
          question: m.question,
          answer: m.answer,
          tags: m.tags || [],
          category: m.category,
          lawReferences: m.law_references || [],
          precedentReferences: m.precedent_references || [],
          lawApiStatus: m.law_api_status || null,
        }))
      );
      if (msgs.length > 0) {
        setCategory(msgs[msgs.length - 1].category);
      }
    } catch {
      if (loadingSessionRef.current === sessionId) {
        setError("메시지를 불러올 수 없습니다.");
      }
    } finally {
      if (loadingSessionRef.current === sessionId) {
        setSessionLoading(false);
      }
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteChatSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        handleNewChat();
      }
    } catch (err) {
      console.error("세션 삭제 실패:", err);
    }
  };

  const handleAsk = async (e) => {
    e?.preventDefault();
    if (!question.trim() || isLoading) return;

    if (!user) {
      navigate("/login", { state: { consultationLogin: true } });
      return;
    }

    const q = question.trim();
    setQuestion("");
    setError("");
    setIsLoading(true);
    setThinkingSteps([]);
    // Reset textarea height
    if (inputRef.current) inputRef.current.style.height = 'auto';

    // Add user question to messages immediately
    const tempId = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        question: q,
        answer: null,
        tags: [],
        category,
        lawReferences: [],
        precedentReferences: [],
        lawApiStatus: null,
        isLoading: true,
      },
    ]);

    try {
      const response = await fetch("/api/ask/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          question: q,
          category,
          chat_session_id: currentSessionId,
        }),
      });

      if (!response.ok) {
        const ct = response.headers.get("content-type") || "";
        let msg = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
        if (ct.includes("application/json")) {
          const ed = await response.json().catch(() => ({}));
          // 백엔드 인증 미들웨어는 message, 그 외는 error 키를 사용
          msg = ed.message || ed.error || msg;
        }
        if (response.status === 401) {
          // 만료/무효 토큰: 정리 후 로그인으로 유도
          localStorage.removeItem("authToken");
          msg = "로그인이 만료되었습니다. 다시 로그인해주세요.";
          setTimeout(() => navigate("/login"), 1200);
        }
        throw new Error(msg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const p = line.slice(6).trim();
          if (p === "[DONE]") continue;
          try {
            const d = JSON.parse(p);

            if (d.type === "thinking" && d.text) {
              setThinkingSteps((prev) => [...prev, d.text]);
            }

            if (d.type === "result") {
              if (d.chat_session_id) {
                setCurrentSessionId(d.chat_session_id);
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === tempId
                    ? {
                        ...m,
                        id: d.query_log_id || tempId,
                        answer: d.answer,
                        tags: d.tags || [],
                        lawReferences: d.law_references || [],
                        precedentReferences: d.precedent_references || [],
                        lawApiStatus: d.law_api_status || null,
                        queryLogId: d.query_log_id,
                        isLoading: false,
                      }
                    : m
                )
              );
              loadSessions();
            }

            if (d.type === "error") {
              throw new Error(d.error);
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes("JSON")) {
              throw parseErr;
            }
          }
        }
      }
    } catch (err) {
      const msg =
        err.message || "서버와 연결할 수 없습니다. 잠시 후 다시 시도해주세요.";
      setError(msg);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeadSubmit = async (leadData) => {
    setIsSubmitting(true);
    try {
      await submitLead(leadData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleAsk();
    }
  };

  // Latest message's law references for right panel
  const latestMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;
  const hasLawData =
    latestMessage &&
    !latestMessage.isLoading &&
    ((latestMessage.lawReferences?.length > 0) ||
      (latestMessage.precedentReferences?.length > 0));

  return (
    <div className="relative flex h-full min-h-0">
      <SEOHead
        title="무료 법률상담 AI - 변호사 상담, 파산, 이혼, 임금체불, 세무"
        description="마이로잇은 AI 기반 무료 법률·세무 상담 서비스입니다. 개인파산, 임금체불, 부당해고, 전세사기, 이혼, 상속, 교통사고, 손해배상 등 법률 문제와 종합소득세, 부가가치세, 양도소득세 등 세무 문제를 24시간 AI가 답변합니다."
        keywords="법률상담,무료법률상담,변호사,AI변호사,법률AI,파산,개인파산,임금체불,부당해고,전세사기,이혼상담,상속,교통사고,손해배상,민사소송,형사소송,계약서,근로기준법,주택임대차,세무상담,종합소득세,부가가치세,양도소득세,증여세,상속세,세무사,프리랜서세금,사업자세금,법률문제,노동법,산업재해,채권추심,신용회복,법률자문,온라인법률상담"
        path="/"
      />
      {/* OAuth 에러 배너 */}
      {oauthError && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-700 px-6 py-3 rounded-xl shadow-lg text-sm animate-fade-in">
          로그인에 실패했습니다. 다시 시도해주세요.
        </div>
      )}
      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div
          className="absolute inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      {user && (
        <div
          className={`
            absolute inset-y-0 left-0 md:relative z-40 h-full w-72 shrink-0
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
            md:translate-x-0
          `}
        >
          <ChatSidebar
            sessions={sessions}
            activeSessionId={currentSessionId}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
            onDeleteSession={handleDeleteSession}
            onClose={() => setSidebarOpen(false)}
          />
        </div>
      )}

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Mobile header bar */}
        {user && (
          <div className="md:hidden flex items-center gap-2 px-4 py-2 border-b border-[#e2d6c8] bg-white/70 backdrop-blur-xl">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="대화 이력 열기"
              className="p-3 text-[#6f6258] hover:bg-white/70 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="text-sm font-medium text-gray-600">
              {currentSessionId ? "대화 중" : "새 대화"}
            </span>
            <button
              onClick={handleNewChat}
              className="p-3 text-[#6f6258] hover:bg-white/70 rounded-lg ml-auto"
              title="새 채팅"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        )}

        {/* Messages area */}
        <div className="chat-scroll flex-1 min-h-0 overflow-y-auto">
          {sessionLoading ? (
            /* Session loading state */
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-[#8a7968]">
                <div className="flex gap-1.5 justify-center mb-3">
                  <span className="w-2.5 h-2.5 bg-[#9b6f45] rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-2.5 h-2.5 bg-[#9b6f45] rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-2.5 h-2.5 bg-[#9b6f45] rounded-full animate-bounce" />
                </div>
                <p className="text-sm">대화를 불러오는 중...</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            /* Welcome state */
            <div className="home-welcome">
              <header className="home-intro">
                <p className="home-eyebrow">나의 첫 법률·세무 길잡이</p>
                <h1>궁금한 법률·세무,<br />편하게 물어보세요.</h1>
                <p className="home-description">법령과 판례를 바탕으로<br className="sm:hidden" /> 필요한 정보를 차근차근 정리해 드려요.</p>
              </header>

                {/* Example questions */}
                <div className="home-examples" aria-label="질문 예시">
                  {EXAMPLE_QUESTIONS.map((example, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setCategory(example.cat);
                        setQuestion(example.q);
                      }}
                      className="home-example"
                      aria-label={example.q}
                    >
                      <span className="home-example-icon">
                        <BrandIcon name={example.icon} size={19} />
                      </span>
                      <span>{example.label}</span>
                      <BrandIcon name="send" size={17} className="ml-auto shrink-0 text-[#8a7968]" />
                    </button>
                  ))}
                </div>

                {/* New Features */}
                <section className="home-tools" aria-labelledby="home-tools-title">
                  <h2 id="home-tools-title">이런 도움도 받을 수 있어요</h2>
                  <div className="grid grid-cols-3 gap-2">
                    {HOME_TOOLS.map((tool) => (
                      <Link key={tool.to} to={tool.to}
                        className="home-tool">
                        <BrandIcon name={tool.icon} size={23} className="text-[#5f554b]" />
                        <span>{tool.label}</span>
                      </Link>
                    ))}
                  </div>
                </section>

              {/* Footer */}
              <footer className="home-legal">
                <span>(주)모비딕컴퍼니</span>
                <div className="flex items-center gap-3">
                  <Link to="/terms">
                    이용약관
                  </Link>
                  <Link to="/privacy">
                    개인정보처리방침
                  </Link>
                </div>
              </footer>
            </div>
          ) : (
            /* Chat messages */
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
              {messages.map((msg, idx) => (
                <div key={msg.id} className="space-y-4">
                  {/* User question */}
                  <div className="flex justify-end">
                    <div className={`max-w-[80%] px-4 py-3 text-white rounded-2xl rounded-br-md shadow-sm ${
                      msg.category === "TAX" ? "bg-[#58664a]" : "bg-[#1f2933]"
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.question}</p>
                    </div>
                  </div>

                  {/* AI answer */}
                  {msg.isLoading ? (
                    <div className="max-w-2xl">
                      <ThinkingProgress
                        loading={true}
                        steps={thinkingSteps.length > 0 ? thinkingSteps : ["법령 정보를 검색하고 AI가 쉽게 풀어서 설명하는 중입니다..."]}
                        interval={thinkingSteps.length > 0 ? 100 : 2500}
                      />
                    </div>
                  ) : msg.answer ? (
                    <AnswerDisplay
                      answer={msg.answer}
                      tags={msg.tags}
                      category={msg.category}
                    />
                  ) : null}

                  {/* Law panel for non-latest answered messages (expandable) */}
                  {!msg.isLoading &&
                    msg.answer &&
                    idx < messages.length - 1 &&
                    (msg.lawReferences?.length > 0 || msg.precedentReferences?.length > 0) && (
                      <InlineLawToggle
                        lawReferences={msg.lawReferences}
                        precedentReferences={msg.precedentReferences}
                        category={msg.category}
                        lawApiStatus={msg.lawApiStatus}
                      />
                    )}
                </div>
              ))}

              {/* Law panel inline on mobile for latest message */}
              {hasLawData && (
                <div className="lg:hidden">
                  <LawSourcePanel
                    lawReferences={latestMessage.lawReferences}
                    precedentReferences={latestMessage.precedentReferences}
                    category={latestMessage.category}
                    lawApiStatus={latestMessage.lawApiStatus}
                  />
                </div>
              )}

              {/* CTA banner for expert consultation */}
              {latestMessage &&
                latestMessage.answer &&
                !latestMessage.isLoading && (
                  <div className="max-w-3xl mx-auto mt-3 mb-2">
                    <button
                      onClick={() => setShowLeadModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/78 border border-[#c9a77a] rounded-2xl text-sm font-semibold text-[#5f554b] hover:bg-white transition-all active:scale-[0.98]"
                    >
                      <BrandIcon name="law" size={18} />
                      <span>전문가 상담 신청하기</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Error display (visible in both welcome and chat states) */}
        {error && (
          <div className="px-4">
            <div className="max-w-3xl mx-auto p-4 bg-[#fff1ef] rounded-2xl border border-[#e9b3a9] text-[#a64232] text-center">
              {error}
            </div>
          </div>
        )}

        {/* Input bar (bottom-fixed, app-like) */}
        <div className="chat-composer shrink-0">
          <div className="max-w-3xl mx-auto">
            {/* Category toggle */}
            <div className="composer-categories" role="group" aria-label="상담 분야">
              <button
                type="button"
                onClick={() => setCategory("LAW")}
                aria-pressed={category === 'LAW'}
                className={`composer-category ${
                  category === "LAW"
                    ? "bg-[#1f2933] text-[#f6f1e8]"
                    : "bg-[#f6f1e8] text-[#6f6258] hover:bg-white"
                }`}
              >
                <span className="inline-flex items-center gap-1.5"><BrandIcon name="law" size={17} /> 법률</span>
              </button>
              <button
                type="button"
                onClick={() => setCategory("TAX")}
                aria-pressed={category === 'TAX'}
                className={`composer-category ${
                  category === "TAX"
                    ? "bg-[#58664a] text-white"
                    : "bg-[#f6f1e8] text-[#6f6258] hover:bg-white"
                }`}
              >
                <span className="inline-flex items-center gap-1.5"><BrandIcon name="tax" size={17} /> 세무</span>
              </button>
            </div>

            {/* Input row */}
            <form onSubmit={handleAsk} className="composer-field">
              <label htmlFor="chat-question" className="sr-only">상담 질문</label>
              <textarea
                id="chat-question"
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="어떤 점이 궁금하신가요?"
                rows={1}
                className="composer-input"
                disabled={isLoading}
              />
              <button
                type="submit"
                aria-label="질문 보내기"
                title="질문 보내기"
                disabled={!question.trim() || isLoading}
                className={`composer-send text-white transition-all active:scale-95 ${
                  !question.trim() || isLoading
                    ? "bg-[#c6b8a8] cursor-not-allowed"
                    : category === "LAW"
                    ? "bg-[#1f2933] hover:bg-[#34414d]"
                    : "bg-[#58664a] hover:bg-[#667758]"
                }`}
              >
                {isLoading ? (
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <BrandIcon name="send" size={22} />
                )}
              </button>
            </form>

            <p className="composer-note">
              AI 참고 정보이며, 법적 효력이 없습니다.
            </p>
          </div>
        </div>
      </div>

      {/* Right panel for law references (desktop only, latest message) */}
      {hasLawData && (
        <div className="hidden lg:block w-96 border-l border-[#e2d6c8] overflow-y-auto bg-white/78">
          <LawSourcePanel
            lawReferences={latestMessage.lawReferences}
            precedentReferences={latestMessage.precedentReferences}
            category={latestMessage.category}
            lawApiStatus={latestMessage.lawApiStatus}
          />
        </div>
      )}

      {/* Lead Form Modal */}
      {showLeadModal && latestMessage && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowLeadModal(false)} />
          <div className="relative bg-[#f6f1e8] w-full max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto border border-[#e2d6c8]" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d6c8]">
              <h3 className="text-base font-bold text-[#1f2933] flex items-center gap-2"><BrandIcon name="law" size={18} /> 전문가 상담 신청</h3>
              <button onClick={() => setShowLeadModal(false)} className="p-1 rounded-full hover:bg-white/70 transition-colors">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-5 py-4">
              <LeadForm
                key={latestMessage.id}
                question={latestMessage.question}
                answer={latestMessage.answer}
                tags={latestMessage.tags}
                category={latestMessage.category}
                queryLogId={latestMessage.queryLogId}
                onSubmit={(data) => handleLeadSubmit(data)}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/** 이전 메시지의 법령 정보를 토글로 보여주는 컴포넌트 */
function InlineLawToggle({
  lawReferences,
  precedentReferences,
  category,
  lawApiStatus,
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="ml-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
      >
        <BrandIcon name="document" size={16} />
        <span>관련 법령 {lawReferences?.length || 0}건{precedentReferences?.length > 0 ? ` · 판례 ${precedentReferences.length}건` : ""}</span>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="mt-2">
          <LawSourcePanel
            lawReferences={lawReferences}
            precedentReferences={precedentReferences}
            category={category}
            lawApiStatus={lawApiStatus}
          />
        </div>
      )}
    </div>
  );
}
